import {
  createDeal,
  type DealResult
} from "../../../../packages/deal-protocol/src";
import {
  formatRoomCode,
  parseRoomCode,
  randomRoomSeed,
  type SessionCodePlayerCount
} from "../../../../packages/session-code/src";
import {
  candidateThemeBundle,
  type CandidateThemeKey
} from "../../../../packages/theme-catalog/src";
import { themeKeyForIndex, themeTitle } from "./themes";
import type {
  CreateSessionInput,
  SessionAdapter,
  SessionBootstrap,
  SessionCard
} from "./types";

const RULES_VERSION = "rules-v0.3";
const DEAL_VERSION = "deal-v1";
const MATCHER_VERSION = "matcher-v1";

function cardMapper(key: CandidateThemeKey): (cardId: string) => SessionCard {
  const { theme } = candidateThemeBundle(key);
  return (cardId) => {
    const definition = theme.cards[cardId];
    if (definition === undefined) {
      throw new Error(`Deal references an unknown card: ${cardId}`);
    }
    return {
      cardId: definition.cardId,
      displayText: definition.displayText,
      cardKind: definition.cardKind
    };
  };
}

/**
 * Reconstruct the full deal from a room code plus the fixed v1 constants this
 * build ships. The normalized room code is the deal's raw seed and the theme
 * index selects the catalog, so every device that scans or types the same code
 * derives the identical deal — same theme, same hands — fully offline.
 */
async function buildSession(
  roomCode: string,
  playerCount: SessionCodePlayerCount,
  themeIndex: number
): Promise<SessionBootstrap> {
  const key = themeKeyForIndex(themeIndex);
  if (key === undefined) {
    throw new Error(`Room code references an unknown theme index: ${themeIndex}`);
  }
  const bundle = candidateThemeBundle(key);
  const deal: DealResult = await createDeal(
    {
      rulesVersion: RULES_VERSION,
      dealVersion: DEAL_VERSION,
      matcherVersion: MATCHER_VERSION,
      catalogRevision: bundle.catalogRevision,
      rawSeed: roomCode,
      playerCount,
      round: 1
    },
    bundle.catalog
  );
  if (
    deal.themeId !== bundle.theme.themeId ||
    deal.themeRevision !== bundle.theme.themeRevision
  ) {
    throw new Error("Deal selected an unexpected candidate theme");
  }
  const card = cardMapper(key);
  const fingerprintHex = deal.dealFingerprint.slice("sha256:".length);
  return {
    sessionId: `${deal.themeId}:${fingerprintHex.slice(0, 16)}`,
    source: "deal",
    playerCount,
    round: 1,
    dealFingerprint: deal.dealFingerprint,
    tableCheckCode: roomCode,
    sessionCode: roomCode,
    theme: {
      themeId: bundle.theme.themeId,
      title: themeTitle(key),
      scenario: bundle.theme.scenario.zh,
      icebreakers: bundle.theme.icebreakers.map((icebreaker) => icebreaker.zh),
      contentLocale: "zh"
    },
    seatDeals: deal.deals.map((seatDeal) => ({
      seat: seatDeal.seat,
      packetId: seatDeal.packetId,
      initial: seatDeal.initial.map(card),
      penalty: seatDeal.penalty.map(card)
    }))
  };
}

export const dealSessionAdapter: SessionAdapter = {
  source: "deal",
  async createSession(input: CreateSessionInput): Promise<SessionBootstrap> {
    if (themeKeyForIndex(input.themeIndex) === undefined) {
      throw new Error(`Unknown theme index: ${input.themeIndex}`);
    }
    const roomCode = formatRoomCode({
      playerCount: input.playerCount,
      themeIndex: input.themeIndex,
      seed: randomRoomSeed()
    });
    return buildSession(roomCode, input.playerCount, input.themeIndex);
  },
  async joinSession(roomCode: string): Promise<SessionBootstrap> {
    const parsed = parseRoomCode(roomCode);
    return buildSession(parsed.code, parsed.playerCount, parsed.themeIndex);
  }
};

export const activeSessionAdapter = dealSessionAdapter;
